import React, { ReactNode, useMemo } from "react";
import styled from "styled-components";
import { Check } from "lucide-react";
import {
  FormattedPlanDetails,
  LanguageNameSpaces,
  UsageType,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { useUserUsageTypes } from "@shared/utils/hooks/misc";
import { useCheckQuotaUsage } from "@shared/features/settings/hooks";
import { getUsageTitleKey } from "@shared/features/settings/utils";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import PrimaryApiHelperTooltip from "features/settings/Billing/PrimaryApiHelperTooltip.tsx";
import SecureClientInviteHelperTooltip from "features/settings/Billing/SecureClientInviteHelperTooltip.tsx";
import InvoicingHelperTooltip from "features/settings/Billing/InvoicingHelperTooltip.tsx";
import WhitelabelHelperTooltip from "features/settings/Billing/WhitelabelHelperTooltip.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens";

// The plan's features in the new look: grouped (bots, trading, tools), each
// row a label on the left and its limit on the right, so the numbers line up
// in one column. Yes/no features close the list as chips.

type GroupKey = "bots" | "trading" | "tools";

const GROUPS: { key: GroupKey; types: UsageType[] }[] = [
  {
    key: "bots",
    types: [
      UsageType.SIGNAL_BOT,
      UsageType.DCA_BOT,
      UsageType.GRID_BOT,
      UsageType.TV_BOT,
      UsageType.HEDGING_BOT,
    ],
  },
  { key: "trading", types: [UsageType.ACTIVE_TRADE, UsageType.MANUAL_TRADE] },
  { key: "tools", types: [UsageType.BACKTESTING, UsageType.CLIENT] },
];

// Short row labels: the group heading already says what they count.
const ROW_LABEL_KEY: {
  [key in UsageType]?: TranslationKeys<LanguageNameSpaces.BILLING>;
} = {
  [UsageType.SIGNAL_BOT]: "plan_features.rows.signals",
  [UsageType.DCA_BOT]: "plan_features.rows.dca",
  [UsageType.GRID_BOT]: "plan_features.rows.grid",
  [UsageType.TV_BOT]: "plan_features.rows.tradingview",
  [UsageType.HEDGING_BOT]: "plan_features.rows.hedging",
  [UsageType.ACTIVE_TRADE]: "plan_features.rows.signal_trades",
  [UsageType.MANUAL_TRADE]: "plan_features.rows.manual_trades",
  [UsageType.BACKTESTING]: "plan_features.rows.backtesting",
  [UsageType.CLIENT]: "plan_features.rows.api_slots",
};

interface NewLookPlanFeaturesProps {
  plan?: FormattedPlanDetails;
  /** Plan cards: one column, tighter spacing. */
  dense?: boolean;
}

const NewLookPlanFeatures = ({ plan, dense }: NewLookPlanFeaturesProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { data: usageLimits } =
    sharedLib.sharedApi.settings.useGetUsageLimitsQuery({
      is_demo_query: false,
    });
  const { getExtraQuota } = useCheckQuotaUsage();
  // The same usage types the classic list shows (some are hidden on purpose).
  const usageTypes: UsageType[] = useUserUsageTypes();
  const planType = plan?.subscription_plan_type;

  const limits = useMemo(
    () => (planType ? usageLimits?.limits?.[planType] : undefined),
    [planType, usageLimits]
  );

  if (!limits) return null;

  const renderValue = (usageType: UsageType): ReactNode => {
    const limit = limits[usageType];
    if (limit === "inf") return t("plan_features.unlimited");
    if (limit === true) return t("plan_features.included");
    const extra = getExtraQuota(usageType);
    return (
      <>
        {limit ?? 0}
        {!!extra && <Extra> +{extra}</Extra>}
      </>
    );
  };

  const groups = GROUPS.map((group) => ({
    ...group,
    types: group.types.filter(
      (type) =>
        usageTypes.includes(type) &&
        limits[type] !== undefined &&
        limits[type] !== false
    ),
  })).filter((group) => group.types.length);

  const included = isWhitelabelSubscriptionType(planType)
    ? [
        {
          key: "client_invite",
          label: tCommon(
            getUsageTitleKey(UsageType.SECURE_CLIENT_INVITE, planType)
          ),
          helper: <SecureClientInviteHelperTooltip planType={planType} />,
        },
        {
          key: "invoicing",
          label: tCommon("tooltips.invoicing_title"),
          helper: <InvoicingHelperTooltip />,
        },
        {
          key: "whitelabel",
          label: tCommon("tooltips.whitelabel_title"),
          helper: <WhitelabelHelperTooltip />,
        },
      ]
    : [];

  return (
    <Wrapper $dense={dense}>
      <Groups $dense={dense}>
        {[groups.slice(0, 1), groups.slice(1)].map((column, i) => (
          <Column key={i}>
            {column.map((group) => (
              <Group key={group.key}>
                <GroupTitle>
                  {t(`plan_features.groups.${group.key}` as const)}
                </GroupTitle>
                {group.types.map((type) => {
                  const labelKey = ROW_LABEL_KEY[type];
                  return (
                    <Row key={type}>
                      <Label>
                        {labelKey && t(labelKey)}
                        {type === UsageType.CLIENT && (
                          <PrimaryApiHelperTooltip />
                        )}
                      </Label>
                      <Value>{renderValue(type)}</Value>
                    </Row>
                  );
                })}
              </Group>
            ))}
          </Column>
        ))}
      </Groups>
      {included.length > 0 && (
        <Included>
          <GroupTitle>{t("plan_features.groups.included")}</GroupTitle>
          <Chips>
            {included.map((item) => (
              <Chip key={item.key}>
                <Check size={12} strokeWidth={2.5} />
                {item.label}
                {item.helper}
              </Chip>
            ))}
          </Chips>
        </Included>
      )}
    </Wrapper>
  );
};

export default NewLookPlanFeatures;

const Wrapper = styled.div<{ $dense?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$dense ? 14 : 20)}px;
`;
// Bots on the left, trading and tools stacked on the right; one column when
// the card is narrow.
const Groups = styled.div<{ $dense?: boolean }>`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$dense ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))"};
  gap: ${(p) => (p.$dense ? "14px" : "20px 32px")};
  > div {
    gap: ${(p) => (p.$dense ? 14 : 20)}px;
  }
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;
const Group = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
const GroupTitle = styled.div`
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  margin-bottom: 6px;
`;
const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  &:last-child {
    border-bottom: 0;
  }
`;
const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 13.5px;
  line-height: 20px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Value = styled.span`
  flex: none;
  font-size: 13.5px;
  line-height: 20px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Extra = styled.span`
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Included = styled.div`
  display: flex;
  flex-direction: column;
`;
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  font-size: 13px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).text};
  svg {
    color: ${(p) => getNewLookTokens(p.theme).up};
  }
`;
